import type { OptionsDocument, QuoteDocument, QuoteTotals } from 'paintscout'
// Deep import, not the builder barrel: this module runs inside the calculator, and the builder
// barrel imports back into the calculator.
import { hasTierProductSwaps } from '../../builder/quote/tiered-packages/apply-tier-products'
import { parseNumber } from '../util/parse-number'
import { resolveTierPrice } from '../util/resolve-tier-price'
import { roundPrice } from '../util'

/**
 * Tiered Packages (Good / Better / Best) — POC.
 *
 * Computes the price delta of the currently-selected tier relative to the base
 * estimate and stores it on `quote.totals.tier_adjustment`. The base tier is the
 * current estimate, so when the selected tier IS the base tier (or the feature is
 * off) the delta is 0.
 *
 * The delta is rendered as its own row on the total table and is folded into the
 * taxable total exactly once, inside `discount()` / `discounts()` (same treatment
 * as addedOptions). Base price is `quote.totals.price` (the quote subtotal,
 * materials included) — the same base `getTierPrices` uses, so the displayed tier
 * prices and the applied total always agree.
 */
export function tierAdjustment(quote: QuoteDocument, _options: OptionsDocument): QuoteTotals {
  const newTotal = { ...quote.totals }

  const basePrice = parseNumber(quote.totals.price)
  const tieredPackages = quote.tieredPackages

  if (
    !tieredPackages ||
    !tieredPackages.enabled ||
    !tieredPackages.tiers ||
    tieredPackages.selectedTierId === tieredPackages.baseTierId ||
    // The selected tier swaps products, so its price is already in `totals.price` — the substrates
    // on the quote changed. Applying the percentage/dollar adjustment on top would charge for the
    // upgrade twice.
    hasTierProductSwaps({ tieredPackages, tierId: tieredPackages.selectedTierId })
  ) {
    // No adjustment applies — clear any stale value rather than writing a 0, so quotes
    // not using the feature keep their original totals shape (and switching back to the
    // base tier removes the field).
    delete newTotal.tier_adjustment
    return newTotal
  }

  const selectedTier = tieredPackages.tiers[tieredPackages.selectedTierId]
  const resolvedPrice = resolveTierPrice(basePrice, selectedTier?.adjustment)

  newTotal.tier_adjustment = roundPrice(resolvedPrice - basePrice)

  return newTotal
}
